import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { post } from "@/lib/api";

const formatScore = (value: number | null) => {
  if (value == null || Number.isNaN(value)) return "--";
  return value.toFixed(0);
};

export type ContentScoreProps = {
  caption: string;
  platform: string;
  assetId?: number | null;
  tags?: string[];
  onBoost?: () => Promise<void> | void;
};

export function ContentScore({ caption, platform, assetId, tags, onBoost }: ContentScoreProps) {
  const [score, setScore] = useState<number | null>(null);
  const [recommendations, setRecommendations] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [boosting, setBoosting] = useState(false);

  const depsKey = useMemo(() => [caption, platform, assetId ?? "", (tags ?? []).join(",")].join("|"), [
    caption,
    platform,
    assetId,
    tags,
  ]);

  useEffect(() => {
    if (!caption.trim()) {
      setScore(null);
      setRecommendations([]);
      return;
    }

    let cancelled = false;
    const handler = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await post("/marketing/score", {
          caption,
          platform,
          asset_id: assetId,
          tags,
        });
        if (!cancelled) {
          setScore(typeof response.quality_score === "number" ? response.quality_score : null);
          setRecommendations(response.recommendations ?? []);
        }
      } catch (error) {
        if (!cancelled) {
          setScore(null);
          setRecommendations([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(handler);
    };
  }, [depsKey]);

  const handleBoost = async () => {
    if (!onBoost) return;
    setBoosting(true);
    try {
      await onBoost();
    } finally {
      setBoosting(false);
    }
  };

  return (
    <Card className="space-y-3 border-border/70 bg-background/50 p-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Content Quality</p>
          <p className="text-sm font-semibold text-foreground">{platform}</p>
        </div>
        <div className="flex flex-col items-end">
          <p className="text-3xl font-bold text-foreground">{formatScore(score)}</p>
          <p className="text-[11px] uppercase text-muted-foreground">Score</p>
        </div>
      </div>
      {loading ? <p className="text-xs text-muted-foreground">Scoring…</p> : null}
      <div className="space-y-2">
        {(recommendations ?? []).slice(0, 3).map((rec) => (
          <Badge key={rec} variant="outline" className="block w-full border-border/60 bg-background/40 text-left text-foreground/80">
            {rec}
          </Badge>
        ))}
      </div>
      {onBoost ? (
        <Button onClick={() => void handleBoost()} disabled={boosting} variant="secondary" className="w-full">
          {boosting ? "Boosting…" : "Boost with Meta Ads"}
        </Button>
      ) : null}
    </Card>
  );
}
